// Strings for release 6 (0026's frontend, spec §12.G): the teacher's monthly
// STARL reflection loop, the counsellor's Safety Valve queue, the student
// check-in's closing Safety Valve question, and the teacher's quiet
// safety_valve_flagged indicator on their results page. Finnish and Swedish
// are drafts pending native-speaker review, same as every other release.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

export const r6En = {
  // Teacher: the monthly reflection screen (web/src/pages/teacher/ReflectionPage.tsx).
  'r6.tr.panelTitle': 'Monthly check-in',
  'r6.tr.panelHint': 'Once a month or so: a quick look at the trend, what might be behind it, and one thing to try.',
  'r6.tr.link': 'Open the monthly check-in',
  'r6.tr.title': (label: string) => `Monthly check-in: ${label}`,
  'r6.tr.beforeTitle': 'Before you look',
  'r6.tr.beforeHint': 'What do you expect this month’s trend to show?',
  'r6.tr.trend.improving': 'Improving',
  'r6.tr.trend.steady': 'About the same',
  'r6.tr.trend.declining': 'Declining',
  'r6.tr.senseTitle': 'The trend',
  'r6.tr.senseWindow': (weeks: number) => `The last ${weeks} weeks, not one check-in.`,
  'r6.tr.youExpected': (trend: string) => `You expected: ${trend}.`,
  'r6.tr.lastCycleTitle': 'Last time',
  'r6.tr.lastCycleBody': (trend: string, kind: string) => `You expected “${trend}” and put it down to ${kind}.`,
  'r6.tr.thinkTitle': 'What might be behind it?',
  'r6.tr.thinkHint': 'Pick what seems closest — this is your own read, not a measurement.',
  'r6.tr.kind.workload': 'Workload',
  'r6.tr.kind.social': 'Social dynamics',
  'r6.tr.kind.external_event': 'Something outside school',
  'r6.tr.kind.seasonal': 'Time of year',
  'r6.tr.kind.unclear': 'Not sure',
  'r6.tr.kind.other': 'Something else',
  'r6.tr.noteLabel': 'Add a note (optional)',
  'r6.tr.actTitle': 'What will you try?',
  'r6.tr.actHint': 'Pick one idea from the playbook to commit to.',
  'r6.tr.playbookLabel': 'Idea',
  'r6.tr.playbookPick': 'Choose one…',
  'r6.tr.save': 'Save',
  'r6.tr.saved': 'Saved.',
  'r6.tr.doneTitle': 'Saved',
  'r6.tr.doneBody': 'Next time you open this, you’ll see whether the trend moved and what you tried.',
  'r6.tr.backToClass': 'Back to the class page',

  // Counsellor: the Safety Valve queue, folded into counsellor_home().
  'r6.cs.title': 'Safety Valve',
  'r6.cs.hint': 'A student asked, anonymously, for a counsellor to check in with their class. This never says who asked.',
  'r6.cs.flagged': (when: string) => `Flagged ${when}`,

  // Student check-in: the closing Safety Valve question.
  'r6.ci.safetyTitle': 'One more thing',
  'r6.ci.safetyHint': 'Would you like a counsellor to check in with your class? This doesn’t say it was you — it just lets them know your class might want someone to check in.',
  'r6.ci.safetyYes': 'Yes, please',
  'r6.ci.safetyNo': 'No, I’m okay',
  'r6.ci.safetyPickFirst': 'Choose Yes or No to continue.',
  'r6.ci.safetyNetMessage':
    'If you want to talk to someone, the school counsellor, the school nurse or any teacher can help. In an emergency, call 112. This tool shows teachers patterns, not individual answers — but you can ask a counsellor to check in with your class at the end of this check-in.',

  // Teacher: the quiet results-page flag (never a push notification).
  'r6.sr.safetyPill': 'Safety Valve',
  'r6.sr.safetyNote': 'Someone in this class asked for a counsellor to check in. The counsellor — and the head of school, as a backup — already know.',

  // Errors — 0026's teacher_log_reflection_cycle / counsellor_update_safety_signal.
  'err.bad_trend': 'Choose what you expect the trend to show.',
  'err.bad_interpretation': 'Choose what seems closest.',
  'err.bad_note': 'The note must be 500 characters or fewer.',
  'err.bad_playbook_key': 'Choose an idea from the playbook.',
};

export type R6Strings = Shape<typeof r6En>;

export const r6Fi: R6Strings = {
  'r6.tr.panelTitle': 'Kuukausikatsaus',
  'r6.tr.panelHint': 'Noin kerran kuussa: nopea katsaus suuntaukseen, mahdolliseen syyhyn ja yhteen kokeiltavaan asiaan.',
  'r6.tr.link': 'Avaa kuukausikatsaus',
  'r6.tr.title': (label) => `Kuukausikatsaus: ${label}`,
  'r6.tr.beforeTitle': 'Ennen kuin katsot',
  'r6.tr.beforeHint': 'Mitä odotat tämän kuukauden suuntauksen näyttävän?',
  'r6.tr.trend.improving': 'Paranee',
  'r6.tr.trend.steady': 'Pysyy samana',
  'r6.tr.trend.declining': 'Heikkenee',
  'r6.tr.senseTitle': 'Suuntaus',
  'r6.tr.senseWindow': (weeks) => `Viimeiset ${weeks} viikkoa, ei vain yksi kysely.`,
  'r6.tr.youExpected': (trend) => `Odotit: ${trend}.`,
  'r6.tr.lastCycleTitle': 'Viime kerralla',
  'r6.tr.lastCycleBody': (trend, kind) => `Odotit ”${trend}” ja arvelit syyksi: ${kind}.`,
  'r6.tr.thinkTitle': 'Mikä voisi olla syynä?',
  'r6.tr.thinkHint': 'Valitse lähinnä osuva — tämä on oma arviosi, ei mittaus.',
  'r6.tr.kind.workload': 'Kuormitus',
  'r6.tr.kind.social': 'Sosiaaliset suhteet',
  'r6.tr.kind.external_event': 'Jokin koulun ulkopuolinen asia',
  'r6.tr.kind.seasonal': 'Vuodenaika',
  'r6.tr.kind.unclear': 'En osaa sanoa',
  'r6.tr.kind.other': 'Jokin muu',
  'r6.tr.noteLabel': 'Lisää muistiinpano (valinnainen)',
  'r6.tr.actTitle': 'Mitä kokeilet?',
  'r6.tr.actHint': 'Valitse yksi idea toimintaoppaasta, johon sitoudut.',
  'r6.tr.playbookLabel': 'Idea',
  'r6.tr.playbookPick': 'Valitse…',
  'r6.tr.save': 'Tallenna',
  'r6.tr.saved': 'Tallennettu.',
  'r6.tr.doneTitle': 'Tallennettu',
  'r6.tr.doneBody': 'Kun avaat tämän seuraavan kerran, näet, muuttuiko suuntaus ja mitä kokeilit.',
  'r6.tr.backToClass': 'Takaisin luokan sivulle',

  'r6.cs.title': 'Turvaventtiili',
  'r6.cs.hint': 'Oppilas pyysi anonyymisti kuraattoria käymään luokan kanssa. Tämä ei koskaan kerro, kuka pyysi.',
  'r6.cs.flagged': (when) => `Merkitty ${when}`,

  'r6.ci.safetyTitle': 'Vielä yksi asia',
  'r6.ci.safetyHint': 'Haluaisitko, että kuraattori käy luokkasi kanssa? Tämä ei kerro, että juuri sinä vastasit — se vain kertoo kuraattorille, että luokkasi saattaisi kaivata käyntiä.',
  'r6.ci.safetyYes': 'Kyllä, kiitos',
  'r6.ci.safetyNo': 'Ei, olen ok',
  'r6.ci.safetyPickFirst': 'Valitse Kyllä tai Ei jatkaaksesi.',
  'r6.ci.safetyNetMessage':
    'Jos haluat puhua jollekin, koulun kuraattori, terveydenhoitaja tai kuka tahansa opettaja voi auttaa. Hätätilanteessa soita 112. Tämä työkalu näyttää opettajille kaavoja, ei yksittäisiä vastauksia — mutta voit pyytää kuraattoria käymään luokkasi kanssa tämän kyselyn lopussa.',

  'r6.sr.safetyPill': 'Turvaventtiili',
  'r6.sr.safetyNote': 'Joku tässä luokassa pyysi kuraattoria käymään. Kuraattori — ja rehtori varalla — tietävät jo.',

  'err.bad_trend': 'Valitse, mitä odotat suuntauksen näyttävän.',
  'err.bad_interpretation': 'Valitse lähinnä osuva vaihtoehto.',
  'err.bad_note': 'Muistiinpanon tulee olla enintään 500 merkkiä.',
  'err.bad_playbook_key': 'Valitse idea toimintaoppaasta.',
};

export const r6Sv: R6Strings = {
  'r6.tr.panelTitle': 'Månadsöversikt',
  'r6.tr.panelHint': 'Ungefär en gång i månaden: en snabb titt på trenden, vad som kan ligga bakom den och en sak att pröva.',
  'r6.tr.link': 'Öppna månadsöversikten',
  'r6.tr.title': (label) => `Månadsöversikt: ${label}`,
  'r6.tr.beforeTitle': 'Innan du tittar',
  'r6.tr.beforeHint': 'Vad förväntar du dig att den här månadens trend visar?',
  'r6.tr.trend.improving': 'Förbättras',
  'r6.tr.trend.steady': 'Oförändrad',
  'r6.tr.trend.declining': 'Försämras',
  'r6.tr.senseTitle': 'Trenden',
  'r6.tr.senseWindow': (weeks) => `De senaste ${weeks} veckorna, inte bara en enkät.`,
  'r6.tr.youExpected': (trend) => `Du förväntade dig: ${trend}.`,
  'r6.tr.lastCycleTitle': 'Förra gången',
  'r6.tr.lastCycleBody': (trend, kind) => `Du förväntade dig ”${trend}” och gissade på: ${kind}.`,
  'r6.tr.thinkTitle': 'Vad kan ligga bakom det?',
  'r6.tr.thinkHint': 'Välj det som stämmer bäst — det här är din egen bedömning, inte en mätning.',
  'r6.tr.kind.workload': 'Arbetsbelastning',
  'r6.tr.kind.social': 'Sociala relationer',
  'r6.tr.kind.external_event': 'Något utanför skolan',
  'r6.tr.kind.seasonal': 'Årstid',
  'r6.tr.kind.unclear': 'Vet inte',
  'r6.tr.kind.other': 'Något annat',
  'r6.tr.noteLabel': 'Lägg till en anteckning (valfritt)',
  'r6.tr.actTitle': 'Vad ska du pröva?',
  'r6.tr.actHint': 'Välj en idé från spelboken att satsa på.',
  'r6.tr.playbookLabel': 'Idé',
  'r6.tr.playbookPick': 'Välj…',
  'r6.tr.save': 'Spara',
  'r6.tr.saved': 'Sparat.',
  'r6.tr.doneTitle': 'Sparat',
  'r6.tr.doneBody': 'Nästa gång du öppnar den här ser du om trenden ändrades och vad du prövade.',
  'r6.tr.backToClass': 'Tillbaka till klassidan',

  'r6.cs.title': 'Säkerhetsventilen',
  'r6.cs.hint': 'En elev bad, anonymt, om att en kurator ska besöka klassen. Det här avslöjar aldrig vem som frågade.',
  'r6.cs.flagged': (when) => `Flaggad ${when}`,

  'r6.ci.safetyTitle': 'En sak till',
  'r6.ci.safetyHint': 'Vill du att en kurator ska besöka din klass? Det här avslöjar inte att det var du — det låter bara kuratorn veta att klassen kan behöva ett besök.',
  'r6.ci.safetyYes': 'Ja, gärna',
  'r6.ci.safetyNo': 'Nej, jag mår bra',
  'r6.ci.safetyPickFirst': 'Välj Ja eller Nej för att fortsätta.',
  'r6.ci.safetyNetMessage':
    'Om du vill prata med någon kan skolkuratorn, skolsköterskan eller vilken lärare som helst hjälpa dig. I en nödsituation, ring 112. Det här verktyget visar lärarna mönster, inte enskilda svar — men du kan be en kurator besöka din klass i slutet av den här enkäten.',

  'r6.sr.safetyPill': 'Säkerhetsventilen',
  'r6.sr.safetyNote': 'Någon i den här klassen bad om att en kurator ska höra av sig. Kuratorn — och rektorn, som backup — vet redan.',

  'err.bad_trend': 'Välj vad du förväntar dig att trenden visar.',
  'err.bad_interpretation': 'Välj det alternativ som stämmer bäst.',
  'err.bad_note': 'Anteckningen måste vara högst 500 tecken.',
  'err.bad_playbook_key': 'Välj en idé från spelboken.',
};
